import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    User,
    Lock,
    CheckCircle,
    Eye,
    EyeOff,
    Loader2,
    ArrowUp,
    LogOut,
    ChevronRight,
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useChangePassword } from '@/api/auth/use-customer-auth';
import { useSnackbar } from 'notistack';
import { useAuth } from '@/contexts/AuthContext';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import KycStatus from '@/pages/portal/profile/components/KycStatus';
import KycUploadForm from '@/pages/portal/profile/components/KycUploadForm';
import { CountrySelect } from '@/components/ui/country-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AddToHomeScreenButton } from '@/components/a2hs/AddToHomeScreenButton';
import { cn } from '@/lib/utils';

interface FormErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
}

const AccordionSection: React.FC<{
    sectionId: string;
    icon: React.ReactNode;
    title: string;
    openSection: string | null;
    onToggle: (id: string) => void;
    children: React.ReactNode;
}> = ({ sectionId, icon, title, openSection, onToggle, children }) => {
    const isOpen = openSection === sectionId;
    return (
        <div className="trader-glass rounded-2xl overflow-hidden">
            <button
                type="button"
                onClick={() => onToggle(sectionId)}
                className="w-full flex items-center justify-between p-4 hover:bg-foreground/5 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        {icon}
                    </div>
                    <span className="font-medium">{title}</span>
                </div>
                <ChevronRight
                    className={cn(
                        'w-5 h-5 text-muted-foreground transition-transform duration-200',
                        isOpen && 'rotate-90'
                    )}
                />
            </button>
            {isOpen && <div className="px-4 pb-4 pt-2 border-t border-border">{children}</div>}
        </div>
    );
};

const MobileProfilePage: React.FC = () => {
    const { t } = useTranslation('profile');
    const { t: tMobile } = useTranslation('mobile');
    const navigate = useNavigate();
    const { profile, updateProfile, isLoading } = useProfile();
    const { enqueueSnackbar } = useSnackbar();
    const { logout, user } = useAuth();
    const changePasswordMutation = useChangePassword();

    const [openSection, setOpenSection] = useState<string | null>(null);
    const toggle = (id: string) => setOpenSection(prev => (prev === id ? null : id));

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        country: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
    });

    React.useEffect(() => {
        setFormData({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phoneNumber: profile.phoneNumber || '',
            country: profile.country || '',
            address: profile.address || '',
            city: profile.city || '',
            state: profile.state || '',
            postalCode: profile.postalCode || '',
        });
    }, [profile]);

    // Reset password fields when security section closes
    React.useEffect(() => {
        if (openSection !== 'security') {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [openSection]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('firstNameRequired');
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = t('firstNameMinLength');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t('lastNameRequired');
        } else if (formData.lastName.trim().length < 2) {
            newErrors.lastName = t('lastNameMinLength');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('emailInvalid');
        }

        if (
            formData.phoneNumber &&
            !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))
        ) {
            newErrors.phoneNumber = t('phoneInvalid');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleCountryChange = (value: string) => {
        setFormData(prev => ({ ...prev, country: value }));
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            enqueueSnackbar(t('fixFormErrors'), { variant: 'error' });
            return;
        }
        try {
            await updateProfile(formData);
        } catch {
            // handled in hook
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            enqueueSnackbar(t('passwordsDoNotMatch'), { variant: 'error' });
            return;
        }
        if (newPassword.length < 8) {
            enqueueSnackbar(t('passwordMinLength'), { variant: 'error' });
            return;
        }

        try {
            await changePasswordMutation.mutateAsync({
                current_password: currentPassword,
                password: newPassword,
                password_confirmation: confirmPassword,
            });
            enqueueSnackbar(t('passwordChangedSuccess'), { variant: 'success' });
            setOpenSection(null);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || t('passwordChangeFailed');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const kycStatus = (profile as any).kycStatus as string | undefined;
    const isVerified = kycStatus === 'approved' || kycStatus === 'verified';
    const isPending = kycStatus === 'pending';

    const initials = [profile.firstName?.[0], profile.lastName?.[0]]
        .filter(Boolean)
        .join('')
        .toUpperCase();

    if (isLoading) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-32">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen text-foreground pb-32">
            <div className="px-4 space-y-4">
                {/* Hero */}
                <div className="flex flex-col items-center pt-8 mb-2">
                    <div className="w-24 h-24 rounded-full trader-glass border-2 border-primary/60 flex items-center justify-center mb-4 shadow-lg">
                        <span className="text-3xl font-bold">{initials || '?'}</span>
                    </div>
                    <h2 className="text-2xl font-bold mb-1">
                        {profile.firstName} {profile.lastName}
                    </h2>
                    <p className="text-muted-foreground text-sm mb-4">{profile.email}</p>
                    <div className="flex gap-2 flex-wrap justify-center">
                        {isVerified ? (
                            <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-xs font-semibold border border-primary/30">
                                Verified
                            </span>
                        ) : isPending ? (
                            <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-semibold border border-amber-500/30">
                                Pending
                            </span>
                        ) : (
                            <span className="px-3 py-1 trader-glass rounded-full text-xs font-semibold text-muted-foreground">
                                Unverified
                            </span>
                        )}
                        {(user as any)?.customer_number && (
                            <span className="px-3 py-1 trader-glass rounded-full text-xs font-semibold text-muted-foreground">
                                #{(user as any).customer_number}
                            </span>
                        )}
                    </div>
                </div>

                {/* Add to Home Screen */}
                <AddToHomeScreenButton variant="card" />

                {/* Personal Information accordion */}
                <AccordionSection
                    sectionId="personal"
                    icon={<User size={20} />}
                    title={t('personalInformation')}
                    openSection={openSection}
                    onToggle={toggle}
                >
                    <form onSubmit={handleProfileSubmit} className="space-y-4 mt-2">
                        <div>
                            <Label htmlFor="firstName" className="text-muted-foreground mb-2 block">
                                {t('firstName')}
                            </Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={cn(
                                    '!bg-background/50 border-border text-foreground',
                                    errors.firstName && 'border-red-500'
                                )}
                                required
                            />
                            {errors.firstName && (
                                <p className="text-trader-negative text-sm mt-1">
                                    {errors.firstName}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="lastName" className="text-muted-foreground mb-2 block">
                                {t('lastName')}
                            </Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={cn(
                                    '!bg-background/50 border-border text-foreground',
                                    errors.lastName && 'border-red-500'
                                )}
                                required
                            />
                            {errors.lastName && (
                                <p className="text-trader-negative text-sm mt-1">
                                    {errors.lastName}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="email" className="text-muted-foreground mb-2 block">
                                {t('email')}
                            </Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={cn(
                                    '!bg-background/50 border-border text-foreground',
                                    errors.email && 'border-red-500'
                                )}
                                required
                            />
                            {errors.email && (
                                <p className="text-trader-negative text-sm mt-1">{errors.email}</p>
                            )}
                        </div>
                        <div>
                            <Label
                                htmlFor="phoneNumber"
                                className="text-muted-foreground mb-2 block"
                            >
                                {t('phoneNumber')}
                            </Label>
                            <Input
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                className={cn(
                                    '!bg-background/50 border-border text-foreground',
                                    errors.phoneNumber && 'border-red-500'
                                )}
                            />
                            {errors.phoneNumber && (
                                <p className="text-trader-negative text-sm mt-1">
                                    {errors.phoneNumber}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="country" className="text-muted-foreground mb-2 block">
                                {t('country')}
                            </Label>
                            <CountrySelect
                                value={formData.country}
                                onValueChange={handleCountryChange}
                                placeholder={t('selectCountry')}
                                className="!bg-background/50 border-border text-foreground"
                            />
                        </div>
                        <div>
                            <Label htmlFor="address" className="text-muted-foreground mb-2 block">
                                {t('address')}
                            </Label>
                            <Input
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder={t('streetAddress')}
                                className="!bg-background/50 border-border text-foreground"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="city" className="text-muted-foreground mb-2 block">
                                    {t('city')}
                                </Label>
                                <Input
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder={t('cityPlaceholder')}
                                    className="!bg-background/50 border-border text-foreground"
                                />
                            </div>
                            <div>
                                <Label htmlFor="state" className="text-muted-foreground mb-2 block">
                                    {t('stateRegion')}
                                </Label>
                                <Input
                                    id="state"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    placeholder={t('stateRegion')}
                                    className="!bg-background/50 border-border text-foreground"
                                />
                            </div>
                        </div>
                        <div>
                            <Label
                                htmlFor="postalCode"
                                className="text-muted-foreground mb-2 block"
                            >
                                {t('postalZip')}
                            </Label>
                            <Input
                                id="postalCode"
                                name="postalCode"
                                value={formData.postalCode}
                                onChange={handleChange}
                                placeholder={t('postalZip')}
                                className="!bg-background/50 border-border text-foreground"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full trader-fab-gradient text-white hover:opacity-90"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    {t('updatingProfile')}
                                </>
                            ) : (
                                t('updateProfile')
                            )}
                        </Button>
                    </form>
                </AccordionSection>

                {/* KYC Verification accordion */}
                <AccordionSection
                    sectionId="kyc"
                    icon={<CheckCircle size={20} />}
                    title={t('kycVerification')}
                    openSection={openSection}
                    onToggle={toggle}
                >
                    <div className="mt-2 space-y-4">
                        <KycStatus />
                        <KycUploadForm />
                    </div>
                </AccordionSection>

                {/* Security accordion */}
                <AccordionSection
                    sectionId="security"
                    icon={<Lock size={20} />}
                    title={t('passwordSecurity')}
                    openSection={openSection}
                    onToggle={toggle}
                >
                    <form onSubmit={handlePasswordChange} className="space-y-4 mt-2">
                        <div>
                            <Label className="text-muted-foreground mb-2 block">
                                {t('currentPassword')}
                            </Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    className="!bg-background/50 border-border text-foreground pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-muted-foreground mb-2 block">
                                {t('newPassword')}
                            </Label>
                            <div className="relative">
                                <Input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    className="!bg-background/50 border-border text-foreground pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                >
                                    {showNewPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-muted-foreground mb-2 block">
                                {t('confirmNewPassword')}
                            </Label>
                            <div className="relative">
                                <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    className="!bg-background/50 border-border text-foreground pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpenSection(null)}
                                disabled={changePasswordMutation.isPending}
                                className="flex-1"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={changePasswordMutation.isPending}
                                className="flex-1 trader-fab-gradient text-white hover:opacity-90"
                            >
                                {changePasswordMutation.isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {t('changingPassword')}
                                    </>
                                ) : (
                                    t('changePassword')
                                )}
                            </Button>
                        </div>
                    </form>
                </AccordionSection>

                {/* Withdraw */}
                <div className="trader-glass rounded-2xl p-4">
                    <button
                        onClick={() => navigate('/deposit-withdraw?action=withdraw')}
                        className="w-full py-3 trader-fab-gradient text-white hover:opacity-90 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                        <ArrowUp className="w-5 h-5" />
                        <span className="font-medium">{t('withdraw')}</span>
                    </button>
                </div>

                {/* Logout */}
                <div className="trader-glass rounded-2xl p-4 mb-6">
                    <button
                        onClick={() => logout()}
                        className="w-full py-3 trader-pill-negative hover:opacity-80 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">{t('logout')}</span>
                    </button>
                </div>
            </div>

            <MobileFloatingNav />
        </div>
    );
};

export default MobileProfilePage;
